import React, { useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "../../../config";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../Context/AuthContext";

function EmployeeHome() {
  const navigate = useNavigate();
  const { isOnBreak, startBreak, resumeBreak, breakStatus } = useAuth();

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch (_) {
      return null;
    }
  }, []);

  const employeeId = storedUser?.employeeId || storedUser?._id || null;

  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [tasks, setTasks] = useState([]);
  const [authChecked, setAuthChecked] = useState(false);

  const [cards, setCards] = useState([
    { id: 1, title: "Total Projects", number: 0 },
    { id: 2, title: "Completed Projects", number: 0 },
    { id: 3, title: "Pending Projects", number: 0 },
  ]);

  useEffect(() => {
    if (!storedUser || !employeeId) {
      navigate("/login");
    } else {
      setAuthChecked(true);
    }
  }, [storedUser, employeeId, navigate]);

  const fetchEmployeeTasks = async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${API_URL}/api/getEmplyeeTask/${employeeId}`, {
        params: { month: selectedMonth, year: selectedYear },
      });
      const sorted = res.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      setTasks(sorted);
    } catch (err) {
      console.error("Error fetching tasks:", err.response?.data || err.message);
      setTasks([]);
    }
  };

  const fetchStats = async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${API_URL}/api/employeeStats/${employeeId}`);
      const data = res.data;
      setCards([
        { id: 1, title: "Total Projects", number: data.projects?.total || 0 },
        { id: 2, title: "Completed Projects", number: data.projects?.completed || 0 },
        { id: 3, title: "Pending Projects", number: data.projects?.pending || 0 },
      ]);
    } catch (err) {
      console.error("Error fetching employee stats:", err.response?.data || err.message);
    }
  };

  useEffect(() => {
    if (!authChecked) return;
    fetchEmployeeTasks();
    fetchStats();
  }, [authChecked, selectedMonth, selectedYear]);

  const getStatusBadge = (status) => {
    if (status === "Completed") return <span className="emp-soft-badge success">{status}</span>;
    if (status === "In Progress") return <span className="emp-soft-badge warning">{status}</span>;
    return <span className="emp-soft-badge info">{status || "Pending"}</span>;
  };

  return (
    <div>
      <h2 className="emp-page-title">Employee Dashboard</h2>
      <p className="emp-page-subtitle">Welcome back, {storedUser?.ename || "Employee"}. Track projects and task health.</p>

      <section className="emp-panel mb-3">
        <div className="emp-panel-body">
          <div className="emp-stat-grid">
            <article className="emp-stat-card bg-light border d-flex flex-column justify-content-between" style={{ minHeight: "120px" }}>
              <div>
                <p className="mb-1 text-muted" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Shift Status</p>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span style={{
                    display: "inline-block",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: isOnBreak ? "#f59e0b" : "#10b981",
                    boxShadow: isOnBreak ? "0 0 8px #f59e0b" : "0 0 8px #10b981"
                  }}></span>
                  <span className="fw-bold" style={{ fontSize: "16px", color: isOnBreak ? "#d97706" : "#059669" }}>
                    {isOnBreak ? "On Break" : "Working"}
                  </span>
                </div>
              </div>
              <button
                className={`btn btn-sm ${isOnBreak ? "btn-success" : "btn-warning"} w-100 fw-semibold`}
                onClick={isOnBreak ? resumeBreak : startBreak}
                style={{ padding: "6px 12px", fontSize: "13px" }}
              >
                {isOnBreak ? "Resume Work" : "Take Break"}
              </button>
            </article>

            {cards.map((card) => (
              <article key={card.id} className="emp-stat-card">
                <p>{card.title}</p>
                <h3>{card.number}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="emp-panel">
        <div className="emp-panel-header">
          <p className="emp-kicker mb-1">Task Overview</p>
          <div className="emp-toolbar">
            <h5 className="mb-0">Recent Tasks</h5>

            <div className="d-flex gap-2 flex-wrap">
              <select
                className="form-select w-auto"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="all">All Months</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("en", { month: "long" })}
                  </option>
                ))}
              </select>

              <select
                className="form-select w-auto"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">All Years</option>
                {[2023, 2024, 2025, 2026].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>

              <button className="btn btn-primary" onClick={() => navigate("/employee/employeeTask")}>
                Open Task Board
              </button>
            </div>
          </div>
        </div>

        <div className="emp-panel-body">
          <div className="emp-table-wrap">
            <table className="table table-hover text-center">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Description</th>
                  <th>Start Date</th>
                  <th>Due Date</th>
                  <th>Time Spent</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-muted py-4">
                      No tasks found for selected filters.
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr key={task._id}>
                      <td className="fw-semibold">{task.title}</td>
                      <td>{task.description || "-"}</td>
                      <td>{task.startDate?.split("T")[0]}</td>
                      <td>{task.dueDate?.split("T")[0]}</td>
                      <td>{(task.timeSpent / 3600).toFixed(1)} hrs</td>
                      <td>{getStatusBadge(task.status)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}

export default EmployeeHome;
